export interface PaymentTemplateVars {
  customerName?: string;
  businessName?: string;
  amount?: number;
  currency?: string;
  description?: string;
  date?: string;
  time?: string;
  dueDate?: string;
  dueDays?: number;
  accountName?: string;
  accountNumber?: string;
  sortCode?: string;
}

const formatAmount = (amount?: number, currency?: string) => {
  if (amount === undefined || amount === null || isNaN(amount)) return "";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency || "GBP",
    }).format(amount);
  } catch {
    return `${currency || ""}${amount}`;
  }
};

/**
 * Render a payment-request template by replacing {{tag}} placeholders.
 * Unknown tags are left intact; empty values produce empty strings, and
 * resulting blank lines are collapsed so the message reads naturally.
 */
export const renderPaymentRequestTemplate = (
  template: string,
  vars: PaymentTemplateVars
): string => {
  const now = new Date();
  let computedDueDate = vars.dueDate;
  if (!computedDueDate && typeof vars.dueDays === "number") {
    if (vars.dueDays <= 0) {
      computedDueDate = "immediately";
    } else {
      const d = new Date(now);
      d.setDate(d.getDate() + vars.dueDays);
      computedDueDate = d.toLocaleDateString("en-GB");
    }
  }
  const values: Record<string, string> = {
    customerName: vars.customerName ?? "",
    businessName: vars.businessName ?? "",
    amount: formatAmount(vars.amount, vars.currency),
    currency: vars.currency ?? "",
    description: vars.description ?? "",
    date: vars.date ?? now.toLocaleDateString("en-GB"),
    time: vars.time ?? now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    dueDate: computedDueDate ?? "",
    accountName: vars.accountName ?? "",
    accountNumber: vars.accountNumber ?? "",
    sortCode: vars.sortCode ?? "",
  };

  let out = (template || "").replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
    return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
  });

  // Collapse 3+ newlines (left over from removed tags) into a clean double break.
  out = out.replace(/\n{3,}/g, "\n\n").trim();
  return out;
};

/**
 * Wrap the rendered plain-text message in a minimal HTML body so it reads
 * like a normal email — no card, no heading, no gradient footer.
 */
export const renderPaymentRequestEmailHtml = (
  message: string,
  options?: { logoUrl?: string; businessName?: string }
): string => {
  const safe = message
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  const logo = options?.logoUrl
    ? `<div style="margin-bottom:16px;"><img src="${options.logoUrl}" alt="${options.businessName ?? ""}" style="max-height:48px;max-width:200px;" /></div>`
    : "";

  return `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#ffffff;">` +
    `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.5;color:#222;max-width:640px;margin:0;padding:24px;text-align:left;white-space:pre-wrap;">` +
    logo +
    safe +
    `</div></body></html>`;
};
